import { parseISO, addDays, format } from "date-fns";
import { DAYS } from "app/utils/constants/settings";

/**
 * CSV export DSOK denného (týždenného) menu.
 *
 * - Anglické názvy stĺpcov.
 * - Meno produktu (a popis) sa exportuje pre KAŽDÝ jazyk, ktorý má zákazník v dátach pripravený
 *   (name_sk, name_en, ...). Jazyky sa zbierajú z titulkov produktov, takže je to pripravené
 *   na ľubovoľné jazyky bez zmeny kódu.
 * - Jeden riadok = jedna položka menu (deň → kategória → produkt).
 * - Bez metadát v obsahu; dátumy od–do a typ menu (po anglicky) sú v názve súboru.
 */

// Kategórie v menu (poradie určuje poradie v CSV). Anglické labely do stĺpca "category".
const CATEGORIES = [
  { key: "soups", label: "Soup" },
  { key: "meals", label: "Main course" },
];

// Anglické názvy dní pre stĺpec "day".
const DAY_NAMES_EN = {
  monday: "Monday",
  tuesday: "Tuesday",
  wednesday: "Wednesday",
  thursday: "Thursday",
  friday: "Friday",
  saturday: "Saturday",
  sunday: "Sunday",
};

// Preferované poradie jazykových stĺpcov; ostatné idú abecedne za nimi.
const LANG_PREFERENCE = ["sk", "cs", "cz", "en", "de", "uk", "pl", "hu"];

const toDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  try {
    return parseISO(String(value));
  } catch (_) {
    return null;
  }
};

const formatDate = (value) => {
  const d = toDate(value);
  return d ? format(d, "dd.MM.yyyy") : "";
};

/** Krátky kód jazyka z kľúča titulku ("sk-SK" → "sk"). */
const langShort = (key) => String(key).split("-")[0].toLowerCase();

/**
 * Zoznam jazykov prítomných v titulkoch produktov: [{ short, key }].
 * `key` je prvý nájdený plný kľúč (napr. "sk-SK") pre daný krátky kód (na spätné dohľadanie).
 */
function collectLanguages(productsById) {
  const map = new Map(); // short -> full key
  Object.values(productsById || {}).forEach((p) => {
    const title = p?.title;
    if (title && typeof title === "object" && !Array.isArray(title)) {
      Object.keys(title).forEach((key) => {
        if (typeof title[key] === "string" && title[key].trim() !== "") {
          const short = langShort(key);
          const existing = map.get(short);
          // Preferuj plný lokálny kľúč (sk-SK) pred krátkym (sk) — editor menu píše
          // plné kľúče, takže tam je najnovšia (editovaná) hodnota názvu.
          if (!existing || (!existing.includes("-") && key.includes("-"))) {
            map.set(short, key);
          }
        }
      });
    }
  });
  const shorts = Array.from(map.keys());
  if (shorts.length === 0) {
    // Fallback, aby CSV vždy malo aspoň jeden name stĺpec.
    return [{ short: "sk", key: "sk-SK" }];
  }
  shorts.sort((a, b) => {
    const ia = LANG_PREFERENCE.indexOf(a);
    const ib = LANG_PREFERENCE.indexOf(b);
    const wa = ia === -1 ? 999 : ia;
    const wb = ib === -1 ? 999 : ib;
    if (wa !== wb) return wa - wb;
    return a.localeCompare(b);
  });
  return shorts.map((short) => ({ short, key: map.get(short) }));
}

/** Vytiahne hodnotu pre daný jazyk z multilang objektu (title/description), tolerantne ku kľúčom. */
function pickLangValue(obj, lang) {
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) return "";
  if (lang.key && typeof obj[lang.key] === "string") return obj[lang.key];
  if (typeof obj[lang.short] === "string") return obj[lang.short];
  const hit = Object.keys(obj).find(
    (k) => langShort(k) === lang.short && typeof obj[k] === "string",
  );
  return hit ? obj[hit] : "";
}

/** Názov pre export = NÁZOV V MENU (title); fallback na webový názov (webTitle), ak title chýba. */
function pickWebName(product, lang) {
  const menuName = pickLangValue(product?.title, lang);
  if (menuName && menuName.trim() !== "") return menuName;
  return pickLangValue(product?.webTitle, lang);
}

/** Váha (kg) → gramy, napr. "300 g". Prázdny string ak nie je. Zhodné so zobrazením v riadku. */
function formatWeightGrams(weightKg) {
  const num = typeof weightKg === "number" ? weightKg : parseFloat(weightKg);
  if (!Number.isFinite(num) || num <= 0) return "";
  return `${Math.round(num * 1000)} g`;
}

/** Objem (l), napr. "0.5 l". Prázdny string ak nie je. */
function formatVolumeLiters(volumeLiters) {
  const num =
    typeof volumeLiters === "number" ? volumeLiters : parseFloat(volumeLiters);
  if (!Number.isFinite(num) || num <= 0) return "";
  return `${parseFloat(num.toFixed(2))} l`;
}

/** Cena s desatinnou čiarkou (slovenský Excel), napr. "1,50". */
function formatPrice(price) {
  if (price === null || price === undefined || price === "") return "";
  const num =
    typeof price === "number"
      ? price
      : parseFloat(String(price).replace(",", "."));
  if (!Number.isFinite(num)) return String(price);
  return num.toFixed(2).replace(".", ",");
}

/** Alergény do "1,3,9". Podporuje pole čísel/stringov aj objektov. */
function formatAllergens(allergens) {
  if (!Array.isArray(allergens) || allergens.length === 0) return "";
  return allergens
    .map((a) =>
      a && typeof a === "object" ? (a.code ?? a.number ?? a.id ?? a.name ?? "") : a,
    )
    .filter((x) => x !== "" && x !== null && x !== undefined)
    .join(",");
}

/** Escape CSV poľa (oddeľovač ";"). */
function csvField(value) {
  const s = value === null || value === undefined ? "" : String(value);
  if (/[";\r\n]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

/** Normalizuje CSV nastavenia (default = všetko zapnuté, názov = váha aj alergény). */
function normalizeCsvOptions(raw) {
  const o = raw && typeof raw === "object" ? raw : {};
  return {
    exportPrice: o.exportPrice !== false,
    exportWeight: o.exportWeight !== false,
    exportDescription: o.exportDescription !== false,
    exportDayName: o.exportDayName !== false,
    exportId: o.exportId !== false,
    nameComposition: Array.isArray(o.nameComposition)
      ? o.nameComposition
      : ["weight", "allergens"],
  };
}

/**
 * Zloží názov jedla podľa nastavenia: [(váha) ]Názov[ (alergény)].
 * Váha vždy v zátvorke PRED názvom, alergény vždy v zátvorke ZA názvom.
 */
function composeMealName(baseName, weightVolume, allergens, nameComposition) {
  let name = baseName || "";
  // Váhu/alergény pridávame len ak je názov vyplnený — inak necháme prázdnu bunku.
  if (name.trim() === "") return "";
  const comp = Array.isArray(nameComposition) ? nameComposition : [];
  if (comp.includes("weight") && weightVolume) {
    name = `(${weightVolume}) ${name}`.trim();
  }
  if (comp.includes("allergens") && allergens) {
    name = `${name} (${allergens})`.trim();
  }
  return name;
}

/**
 * Postaví CSV z menu.
 * @param {object} menu - menu objekt (weekStartDate, menuType, days, productsById)
 * @param {object} [opts]
 * @param {(id: string) => object|null} [opts.resolveProduct] - vráti (obohatený) produkt pre id;
 *        default = menu.productsById[id].
 * @returns {{ filename: string, content: string }}
 */
export function buildDailyMenuCsv(menu, opts = {}) {
  const productsById = menu?.productsById || {};
  const resolveProduct =
    typeof opts.resolveProduct === "function"
      ? opts.resolveProduct
      : (id) => productsById[id] || null;
  const options = normalizeCsvOptions(opts.options);

  const languages = collectLanguages(productsById);

  // Hlavička podľa nastavení (deň/cena/váha/popis sa dajú vypnúť). Alergény nie sú samostatný
  // stĺpec — vstupujú do názvu jedla podľa „Tvorba názvu jedla“.
  const header = [];
  if (options.exportDayName) header.push("day");
  header.push("date", "category", "order");
  languages.forEach((l) => header.push(`name_${l.short}`));
  if (options.exportPrice) header.push("price");
  if (options.exportWeight) header.push("weight_volume");
  if (options.exportDescription) {
    languages.forEach((l) => header.push(`description_${l.short}`));
  }
  if (options.exportId) header.push("id");

  const rows = [header];

  DAYS.forEach((dayKey) => {
    const dayData = menu?.days?.[dayKey];
    if (!dayData) return;
    const dayLabel = DAY_NAMES_EN[dayKey] || dayKey;
    const dateLabel = formatDate(dayData.date);

    CATEGORIES.forEach((cat) => {
      const ids = Array.isArray(dayData[cat.key]) ? dayData[cat.key] : [];
      let orderNo = 0;
      ids.forEach((id) => {
        const product = resolveProduct(id);
        if (!product) return;
        // Do exportu idú len produkty označené „zobraziť na webe".
        if (product.showOnWeb === false) return;
        orderNo += 1;

        const weightVolume =
          formatWeightGrams(product.weightKg) ||
          formatVolumeLiters(product.volumeLiters);
        const allergens = formatAllergens(product.allergens);

        // Názov jedla per jazyk — z webového názvu (webTitle), zložený podľa nastavenia.
        const names = languages.map((l) =>
          composeMealName(
            pickWebName(product, l),
            weightVolume,
            allergens,
            options.nameComposition,
          ),
        );

        const row = [];
        if (options.exportDayName) row.push(dayLabel);
        row.push(dateLabel, cat.label, orderNo);
        names.forEach((n) => row.push(n));
        if (options.exportPrice) row.push(formatPrice(product.price));
        if (options.exportWeight) row.push(weightVolume);
        if (options.exportDescription) {
          languages.forEach((l) =>
            row.push(pickLangValue(product.description, l)),
          );
        }
        if (options.exportId) row.push(product.id ?? id);

        rows.push(row);
      });
    });
  });

  const content =
    "﻿" + rows.map((r) => r.map(csvField).join(";")).join("\r\n");

  return { filename: buildFilename(menu), content };
}

/** Názov súboru: daily-menu_<typ>_<od>_<do>.csv (dátumy DD.MM.RRRR, typ po anglicky). */
function buildFilename(menu) {
  const type = (menu?.menuType && String(menu.menuType).trim()) || "daily-menu";
  const start = toDate(menu?.weekStartDate);
  const from = start ? format(start, "dd.MM.yyyy") : "";
  const to = start ? format(addDays(start, 6), "dd.MM.yyyy") : "";
  const range = from && to ? `${from}_${to}` : from || "menu";
  return `daily-menu_${type}_${range}.csv`.replace(/\s+/g, "-");
}

/** Stiahne CSV súbor v prehliadači. */
export function downloadDailyMenuCsv(menu, opts = {}) {
  const { filename, content } = buildDailyMenuCsv(menu, opts);
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
  return filename;
}
